<template>
  <div
    class="timeline timeline-hidden"
    id="scrollable-timeline"
    @mouseleave="
      () => {
        clicking = false
      }
    "
    @mousedown="
      (e) => {
        e.preventDefault()
        previousX = e.clientX
        clicking = true
      }
    "
    @mousemove="panTimeline"
  >
    <div id="current-time-indicator"></div>
    <div id="timelines-wrapper">
      <div
        class="one-satellite-timeline"
        v-for="satellite in satEvents"
        :key="satellite.satId"
      >
        <span class="satellite-id">{{ satellite.satId }}</span>
        <div class="burns-events-wrapper">
          <span class="events-type">Burns</span>
          <div
            class="event-wrapper"
            v-for="burn in satellite.burns"
            :key="burn.id"
            v-bind="attrs"
            v-on="on"
            @click="() => attrs"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="progress-wrapper" v-bind="attrs" v-on="on">
                  <v-progress-linear
                    :value="burn.progress"
                    height="10"
                    :color="getColor(burn.progress, 'burn')"
                    @click="goToDate(burn.start)"
                  />
                </div>
              </template>
              <span> {{ burn.id }} </span>
            </v-tooltip>
          </div>
        </div>
        <div class="eclipses-events-wrapper">
          <span class="events-type">Eclipses</span>
          <div
            class="event-wrapper"
            v-for="eclipse in satellite.eclipses"
            :key="eclipse.id"
            v-bind="attrs"
            v-on="on"
            @click="() => attrs"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="progress-wrapper" v-bind="attrs" v-on="on">
                  <v-progress-linear
                    :value="0"
                    height="10"
                    :color="getColor(0, 'eclipse')"
                    @click="goToDate(eclipse.start)"
                  />
                </div>
              </template>
              <span> {{ eclipse.id }} </span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>

    <br />
    <br />
    <div id="time-axis">
      <div v-for="tick in ticks" :key="tick.valueOf()" class="tick-wrapper">
        <div class="tick">
          <span>{{ formatDate(tick) }}</span>
        </div>
      </div>
    </div>
    <div id="toggle-auto-scroll">
      <v-checkbox
        v-model="autoScroll"
        dense
        hide-details
        :dark="true"
        color="info"
        :label="`Auto scroll is: ${autoScroll ? 'on' : 'off'}`"
      ></v-checkbox>
    </div>
    <div id="toggle-timeline-button" @click="toggleTimeline">
      <svg
        id="toggle-timeline-icon"
        xmlns="http://www.w3.org/2000/svg"
        height="28px"
        viewBox="0 0 24 24"
        width="28px"
        fill="#FFFFFF"
        style="display: block; margin: auto"
      >
        <path d="M24 24H0V0h24v24z" fill="none" opacity="1" />
        <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z" />
      </svg>
    </div>
  </div>
</template>

<script>
import './Timeline.scss'
import * as Cesium from 'cesium'
export default {
  name: 'timeline',
  props: [
    'satEvents',
    'initialDate',
    'finalDate',
    'clock',
    'updateSatelliteState',
    'toggleTimeline',
    'hideTimeline'
  ],
  data() {
    return {
      autoScroll: true,
      clicking: false,
      previousX: 0,
      ticks: [],
      pxToMsRatio: 0,
      timeZoom: 1,
      attrs: '',
      msToPx: 1,
      on: '',
      lastUpdated: 0
    }
  },

  computed: {
    totalWidth() {
      return this.timeZoom * 1e4
    },
    timeStep() {
      return (1 / this.timeZoom) * 3600 * 1000
    }
  },

  mounted() {
    // update positions on resize for
    window.addEventListener('resize', this.updatePositions)
    // listens for mouseup to detect when user is not trying to scroll the timeline anymore
    document.addEventListener('mouseup', () => (this.clicking = false))

    const timeline = document.querySelector('#scrollable-timeline')
    // positions date ticks on timeline's axis, then hide the timeline
    this.updateTicks().then(() => this.hideTimeline())

    // listens for user's mouse wheel events to allow zooming on timeline
    timeline.addEventListener('wheel', this.zoomTimeline)

    // initializes last updated variable
    this.lastUpdated = Cesium.JulianDate.fromDate(this.initialDate)
    this.$parent.updateElementsStyle('#FirstViewer')
  },

  updated() {
    // Component is mounted before satEvents are loaded. Need to update positions after they were passed as props
    this.updatePositions()
    // avoids having the same event triggering multiple times
    this.clock.onTick.removeEventListener(this.moveTimeline)
    this.clock.onTick.addEventListener(this.moveTimeline)
  },

  methods: {
    /**
     * This function positions ticks on the timeline's axis
     * Time step is one hour by default and changed when user zooms in or out
     */
    async updateTicks() {
      document.querySelector('#time-axis').style.width = `${this.totalWidth}px`
      // adds extra time step duration for a prettier design
      const totalDurationInMs =
        this.finalDate.valueOf() - this.initialDate.valueOf() + this.timeStep
      // resets ticks array before filling it with new ticks
      this.ticks = []
      for (let t = 0; t < totalDurationInMs; t += this.timeStep) {
        this.ticks.push(new Date(this.initialDate.valueOf() + t))
      }
    },

    /**
     * This functions scrolls the timeline to center it around the current Cesium date
     */
    moveTimeline(clock, force = false) {
      const timePassed = Cesium.JulianDate.secondsDifference(
        clock.currentTime,
        this.lastUpdated
      )
      if ((Math.abs(timePassed) > 0 && this.autoScroll === true) || force) {
        const timeline = document.querySelector('#scrollable-timeline')
        const currDate = Cesium.JulianDate.toDate(clock.currentTime)
        // allows smooth scrolling when user clicks on an event or on cesium's built-in timeline
        let behavior =
          Math.abs(Math.abs(timePassed) - Math.abs(clock.multiplier) / 15) >
          Math.abs(clock.multiplier)
            ? 'smooth'
            : 'auto'
        timeline.scrollTo({
          left: (currDate.valueOf() - this.initialDate.valueOf()) * this.msToPx,
          behavior: behavior
        })
        this.lastUpdated = clock.currentTime
      }
    },

    /**
     * This functions scrolls the timeline on user's interaction
     */
    panTimeline(e) {
      e.preventDefault()
      if (this.clicking) {
        let scrollable = document.querySelector('#scrollable-timeline')
        scrollable.scrollLeft =
          scrollable.scrollLeft + (this.previousX - e.clientX)
        this.previousX = e.clientX
      }
    },

    getColor(progress, type) {
      if (type === 'eclipse') {
        return 'grey'
      } else if (progress === 100) {
        return 'green'
      } else {
        return 'blue'
      }
    },

    /**
     * Positions ticks and spacecraft events on the timeline
     */
    updatePositions() {
      const timeAxis = document.querySelector('#time-axis')
      const timeline = document.querySelector('#scrollable-timeline')
      timeAxis.style.width = `${this.totalWidth + timeline.offsetWidth}px`
      // timeAxis.style.height = `${timeAxis.offsetTop}px`
      const totalDurationInMs =
        this.finalDate.valueOf() - this.initialDate.valueOf() + this.timeStep

      this.msToPx = this.totalWidth / totalDurationInMs

      const tickElements = document.querySelectorAll('.tick-wrapper')
      tickElements.forEach((tickEl, index) => {
        const tickDate = this.ticks[index]
        tickEl.style.left = `${
          (tickDate.valueOf() - this.initialDate.valueOf()) * this.msToPx -
          tickEl.offsetWidth / 2 +
          timeline.offsetWidth / 2
        }px`
      })

      const timelineElements = document.querySelectorAll(
        '.one-satellite-timeline'
      )

      // loop through all timelines (one sat = one timeline)
      timelineElements.forEach((oneSatTL, satIndex) => {
        // updates tl width
        oneSatTL.style.width = `${timeAxis.offsetWidth}px`
        // loop through timeline's children nodes
        for (let i = 0; i < oneSatTL.children.length; i++) {
          const oneSatChildNode = oneSatTL.children[i]
          // If not an events-wrapper, increment counter
          if (
            oneSatChildNode.classList.toString().includes('-events-wrapper')
          ) {
            // extract the type of events
            const type = oneSatChildNode.classList
              .toString()
              .match(/.+?(?=-events-wrapper)/)[0]
            // initializes counter of elements positionned before the first event wrapper node in the DOM
            let nbOfElBeforeFirstEventWrapper = 0
            // loop through events
            for (let k = 0; k < oneSatChildNode.children.length; k++) {
              const eventWrapper = oneSatChildNode.children[k]
              if (
                !eventWrapper.classList.toString().includes('event-wrapper')
              ) {
                nbOfElBeforeFirstEventWrapper += 1
              } else {
                const satEvent =
                  this.satEvents[satIndex][type][
                    k - nbOfElBeforeFirstEventWrapper
                  ]
                const startDate = Date.parse(satEvent.start)
                const endDate = Date.parse(satEvent.end)
                eventWrapper.style.left = `${
                  (startDate.valueOf() - this.initialDate.valueOf()) *
                    this.msToPx +
                  timeline.offsetWidth / 2
                }px`
                eventWrapper.style.width = `${
                  (endDate.valueOf() - startDate.valueOf()) * this.msToPx
                }px`
              }
            }
          }
        }
      })
    },

    /**
     * Zooms timeline
     */
    zoomTimeline(e) {
      e.preventDefault()
      if (e.deltaY < 0 && this.timeZoom < 8) {
        this.timeZoom *= 2
      } else if (e.deltaY > 0 && this.timeZoom >= 0.25) {
        this.timeZoom /= 2
      }
      this.updateTicks()
      this.moveTimeline(this.clock, true)
    },

    formatDate(date) {
      return date.toISOString().replace(/\.000Z/, '')
    },

    /**
     * Sets Cesium's clock current date to the date passed as argument
     */
    goToDate(date) {
      this.clock.currentTime = Cesium.JulianDate.fromIso8601(date)
      // forces satellite state update to match the new current date
      this.updateSatelliteState(
        this.clock,
        {},
        new Date(),
        new Date(),
        new Date(),
        true
      )
      this.moveTimeline(this.clock, true)
    }
  },
  beforeDestroy() {
    this.clock.onTick.removeEventListener(this.moveTimeline)
  }
}
</script>

<style scoped></style>
